import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import { LoadingSpinner as Spinner } from "@/components/ui/LoadingSpinner";

const CONFIRM_EMAIL_MUTATION = gql`
  mutation ConfirmEmail($token: String!) {
    confirmEmail(token: $token) {
      token
      user {
        id
      }
      errors
    }
  }
`;

const RESEND_CONFIRMATION_MUTATION = gql`
  mutation ResendConfirmation($email: String!) {
    resendConfirmation(email: $email) {
      success
      errors
    }
  }
`;

export function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { setToken } = useAuth();

  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [resendEmail, setResendEmail] = useState("");
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrors(["Trūksta patvirtinimo kodo."]);
      setLoading(false);
      return;
    }

    graphqlClient
      .request<{
        confirmEmail: {
          token: string | null;
          user: { id: string } | null;
          errors: string[];
        };
      }>(CONFIRM_EMAIL_MUTATION, { token })
      .then(async (data) => {
        if (data.confirmEmail.token && data.confirmEmail.user) {
          await setToken(data.confirmEmail.token);
          navigate("/");
        } else {
          setErrors(data.confirmEmail.errors);
        }
      })
      .catch(() => {
        setErrors(["Įvyko klaida. Bandykite dar kartą."]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, navigate, setToken]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendLoading(true);

    try {
      await graphqlClient.request(RESEND_CONFIRMATION_MUTATION, {
        email: resendEmail,
      });
      setResendSent(true);
    } catch {
      // Ignore errors to prevent email enumeration
      setResendSent(true);
    } finally {
      setResendLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/icon.svg" alt="Boardo" className="h-12 w-12" />
            <span className="text-2xl font-bold text-primary-800">Boardo</span>
          </Link>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            El. pašto patvirtinimas
          </h1>
        </div>

        <div className="card p-8">
          {errors.length > 0 && (
            <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}

          {resendSent ? (
            <div className="rounded-lg bg-green-50 p-4 text-center text-sm text-green-700">
              Jei šis el. pašto adresas egzistuoja, išsiuntėme naują patvirtinimo nuorodą.
            </div>
          ) : (
            <div>
              <p className="mb-4 text-sm text-gray-600">
                Įveskite savo el. pašto adresą ir mes išsiųsime naują patvirtinimo nuorodą.
              </p>
              <form onSubmit={handleResend} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    El. paštas
                  </label>
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="input"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={resendLoading}
                  className="btn-primary w-full"
                >
                  {resendLoading
                    ? "Siunčiama..."
                    : "Siųsti naują patvirtinimo nuorodą"}
                </button>
              </form>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary-800 hover:text-primary-700"
            >
              Grįžti į prisijungimą
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
